/****************************************************
 *
 * 菜单页面生命周期：读取隔离、返回刷新与滚动恢复。
 *
 * @author Francis
 * @since 2026-10-09
 **************************************************/
import { markRaw, nextTick, onActivated, onBeforeUnmount, onDeactivated, reactive, toRaw } from 'vue'
import { contentLoadingState, paintContentFrame } from './contentLoading.mjs'
import { createPageReadScope, isPageReadCanceled, withPageRequestContext } from './pageReadScope.mjs'
import { getComponentPageCachePolicy, getPageCachePolicy, isPageCacheable } from './pageCachePolicy.mjs'
import { findPageChild } from './pageCacheState.mjs'
import { isAnimalProfileTabOnlyChange, resolvePageViewIdentity } from './pageViewIdentity.mjs'

const runtimes = new WeakMap()
const instanceStatuses = new WeakMap()
const preparedComponents = new WeakMap()
const listLoadingKeys = new Set(['loading4Table', 'loading4Dropdown', 'loading', 'listLoading', 'loadingList'])

function currentRoute(vm) {
  return vm.$router?.currentRoute?.value || vm.$route
}

function routeSnapshot(route) {
  return { ...route, query: { ...(route?.query || {}) }, params: { ...(route?.params || {}) }, meta: { ...(route?.meta || {}) } }
}

function matchesRoute(state) {
  const route = currentRoute(state.vm)
  return Boolean(state.active && route?.path?.toLowerCase() === state.path
    && isPageCacheable(route) === state.cachedMode)
}

function ownerState(vm) {
  let instance = vm
  while (instance) {
    const state = runtimes.get(instance)
    if (state) return state.owner || state
    instance = instance.$parent
  }
  return null
}

/** watcher 可在 created 前读取 computed；稳定占位为首次空 scope 保留响应式依赖。 */
function ensureInstanceStatus(vm) {
  let status = instanceStatuses.get(vm)
  if (!status) {
    status = reactive({ refreshing: false, error: '', scope: null })
    instanceStatuses.set(vm, status)
  }
  return status
}

/** 初始化前后遵循同一归属规则，独立页面不提前缓存父页面的 scope。 */
function resolveInstanceStatus(vm) {
  const state = runtimes.get(vm)
  if (state) return (state.owner || state).status
  const ancestor = ownerState(vm.$parent)
  const policy = getComponentPageCachePolicy(vm.$options.name || vm.$options.__name)
  if (policy && !(policy.shared || ancestor?.policy.child === policy.componentName)) {
    return ensureInstanceStatus(vm)
  }
  return ancestor?.status || ensureInstanceStatus(vm)
}

function renewScope(state) {
  state.scope?.close()
  state.viewIdentity = resolvePageViewIdentity(currentRoute(state.vm))
  state.refreshTicket += 1
  state.status.refreshing = false
  state.status.error = ''
  state.rollbackRefresh = null
  state.scope = createPageReadScope({
    loadingScope: contentLoadingState.value.scopeId,
    resourceId: currentRoute(state.vm)?.meta?.resourceId || '',
    route: routeSnapshot(currentRoute(state.vm)),
    isCurrent: () => matchesRoute(state),
  })
  state.status.scope = markRaw(state.scope)
  return state.scope
}

/** 只代理页面普通数据；Grid、DOM、Map 等有自身实例身份的对象保持原样。 */
function guardedData(value, mayWrite, background, cache = new WeakMap()) {
  if (!value || typeof value !== 'object') return value
  const raw = toRaw(value)
  if (raw.__v_skip || (Object.getPrototypeOf(raw) !== Object.prototype && !Array.isArray(raw))) return value
  if (cache.has(value)) return cache.get(value)
  const proxy = new Proxy(value, {
    get(target, key, receiver) {
      return guardedData(Reflect.get(target, key, receiver), mayWrite, background, cache)
    },
    set(target, key, next) {
      if (!mayWrite()) return true
      if (background && listLoadingKeys.has(key) && next === true) return true
      return Reflect.set(target, key, next)
    },
    deleteProperty(target, key) {
      return mayWrite() ? Reflect.deleteProperty(target, key) : true
    },
  })
  cache.set(value, proxy)
  return proxy
}

function messageProxy(message, state, scope, background) {
  if (!message) return message
  const invoke = (method, args) => {
    if (!scope?.isActive()) return undefined
    if (background && method === 'error') {
      state.status.error = typeof args[0] === 'string' ? args[0] : args[0]?.message || '数据更新失败，请重试'
      state.rollbackRefresh?.()
      return undefined
    }
    return method ? message[method]?.(...args) : message(...args)
  }
  return new Proxy(message, {
    apply: (_target, _receiver, args) => invoke('', args),
    get: (target, key) => typeof target[key] === 'function' ? (...args) => invoke(key, args) : target[key],
  })
}

/** 同组件、作用域和加载模式复用数据代理，保留协调器对状态对象的身份判断。 */
function scopedDataCache(componentState, scope, background) {
  if (!componentState || !scope) return new WeakMap()
  let caches = componentState.dataCachesByScope.get(scope)
  if (!caches) {
    caches = { foreground: new WeakMap(), background: new WeakMap() }
    componentState.dataCachesByScope.set(scope, caches)
  }
  return background ? caches.background : caches.foreground
}

/** 每次方法调用固定自己的 scope，后续 await 不会切换到返回后的新 scope。 */
function scopedInstance(vm, scope, background = false) {
  const state = ownerState(vm)
  const componentState = runtimes.get(vm)
  const dataCache = scopedDataCache(componentState, scope, background)
  let proxy
  proxy = new Proxy(vm, {
    get(target, key) {
      if (key === '$pageReadScope') return scope
      if (key === '$pageIsActive') return () => Boolean(scope?.isActive())
      if (key === '$pageRequest') return (starter, options = {}) => scope.request(starter, { background, ...options })
      if (key === '$pageCacheRestoring') return background
      if (key === '$route') return scope?.route || target.$route
      if (key === '$message') return messageProxy(target.$message, state, scope, background)
      const original = componentState?.originalMethods.get(key)
      if (original) return (...args) => invokeMethod(vm, key, args, scope, background)
      const value = Reflect.get(target, key)
      if (typeof key === 'string' && key.startsWith('$')) return value
      return guardedData(value, () => Boolean(state.cleanup || scope?.isActive()), background, dataCache)
    },
    set(target, key, value) {
      if (!state.cleanup && !scope?.isActive()) return true
      if (background && listLoadingKeys.has(key) && value === true) return true
      return Reflect.set(target, key, value)
    },
  })
  return proxy
}

function invokeMethod(vm, name, args, scope = ownerState(vm)?.scope, background = ownerState(vm)?.status.refreshing) {
  const state = ownerState(vm)
  const original = runtimes.get(vm)?.originalMethods.get(name)
  if (!original || (!state.cleanup && !scope?.isActive())) return undefined
  try {
    const result = withPageRequestContext({ ...scope, background }, () => original.apply(scopedInstance(vm, scope, background), args))
    if (!result?.then) return result
    return result.catch((error) => {
      if (isPageReadCanceled(error) || !scope.isActive()) return undefined
      if (background) state.rollbackRefresh?.()
      throw error
    })
  } catch (error) {
    if (isPageReadCanceled(error) || !scope?.isActive()) return undefined
    if (background) state.rollbackRefresh?.()
    throw error
  }
}

function walkPlainData(vm, visit) {
  const visited = new WeakSet()
  const walk = (object, path, depth) => {
    if (!object || typeof object !== 'object' || visited.has(object) || depth > 6) return
    const raw = toRaw(object)
    if (raw.__v_skip || Object.getPrototypeOf(raw) !== Object.prototype) return
    visited.add(object)
    Object.keys(object).forEach((key) => {
      const value = object[key]
      visit(object, key, value, [...path, key])
      if (!Array.isArray(value)) walk(value, [...path, key], depth + 1)
    })
  }
  walk(vm.$data, [], 0)
}

function invalidateSequences(vm) {
  walkPlainData(vm, (object, key, value) => {
    if (typeof value === 'number' && /(?:sequence|revision)$/i.test(key)) object[key] += 1
    if (listLoadingKeys.has(key) && typeof value === 'boolean') object[key] = false
  })
}

function captureRows(vm) {
  const values = []
  walkPlainData(vm, (object, key, value) => {
    if (Array.isArray(value) && /^(?:data|rows|sourceRows|selectedRow|selected)$/.test(key)) values.push([object, key, value])
    if (typeof value === 'number' && /^(?:total|totalRecords|totalCount)$/.test(key)) values.push([object, key, value])
  })
  return () => values.forEach(([object, key, value]) => { object[key] = value })
}

function scrollNodes(vm) {
  if (!vm.$el?.querySelectorAll) return []
  return [...vm.$el.querySelectorAll('.ag-body-viewport, .ag-body-horizontal-scroll-viewport, .p-treetable-wrapper, .el-scrollbar__wrap, [data-page-scroll]')]
}

function captureScroll(vm) {
  return scrollNodes(vm).map((node) => ({ node, top: node.scrollTop, left: node.scrollLeft }))
}

function restoreScroll(snapshot) {
  snapshot?.forEach(({ node, top, left }) => {
    if (!node.isConnected) return
    node.scrollTop = top
    node.scrollLeft = left
  })
}

function trackRefreshScroll(vm) {
  let scroll = captureScroll(vm)
  let frame = null
  const root = vm.$el
  const events = ['wheel', 'touchend', 'pointerup', 'keydown']
  const record = () => {
    if (frame !== null) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      frame = null
      scroll = captureScroll(vm)
    })
  }
  events.forEach((event) => root?.addEventListener?.(event, record, { passive: true }))
  return {
    restore: () => restoreScroll(scroll),
    stop() {
      if (frame !== null) cancelAnimationFrame(frame)
      events.forEach((event) => root?.removeEventListener?.(event, record))
    },
  }
}

function cacheRoot(vm) {
  const state = runtimes.get(vm)
  return state && !state.owner ? state : null
}

export function getPageNavigationState(vm) {
  return ownerState(vm?.$?.proxy || vm)?.status || null
}

export async function refreshPageForNavigation(vm) {
  vm = vm?.$?.proxy || vm
  const state = cacheRoot(vm)
  if (!state || !state.scope.isActive() || state.status.refreshing) return
  const scope = state.scope
  const ticket = ++state.refreshTicket
  state.status.refreshing = true
  state.status.error = ''
  const scroll = trackRefreshScroll(vm)
  const restoreOwnRows = captureRows(vm)
  const child = state.policy.child ? findPageChild(vm, state.policy.child) : null
  const restoreChildRows = child ? captureRows(child) : () => {}
  const restoreRows = () => { restoreOwnRows(); restoreChildRows() }
  state.rollbackRefresh = () => {
    if (scope.isActive() && ticket === state.refreshTicket) restoreRows()
  }
  try {
    // 先恢复已有内容并让浏览器绘制，再开始后台刷新。
    await paintContentFrame()
    scope.assertActive()
    if (!state.initialized && state.mountedHooks.length) {
      await runMounted(vm, state.mountedHooks)
    } else {
      // 显式区分导航读取与用户同时发起的操作，避免表单/列表错误回滚导航结果。
      const refreshTarget = state.policy.foregroundActions ? scopedInstance(vm, scope, true) : vm
      await state.policy.refresh(refreshTarget, { background: true, scope, isCurrent: () => scope.isActive() })
    }
    if (!scope.isActive() || ticket !== state.refreshTicket) return
    if (state.status.error) restoreRows()
  } catch (error) {
    if (isPageReadCanceled(error) || !scope.isActive() || ticket !== state.refreshTicket) return
    restoreRows()
    state.status.error = error?.message || '数据更新失败，请重试'
  } finally {
    scroll.stop()
    if (ticket === state.refreshTicket) state.rollbackRefresh = null
    if (scope.isActive() && ticket === state.refreshTicket) {
      await nextTick()
      scroll.restore()
      state.status.refreshing = false
    }
  }
}

async function runMounted(vm, hooks) {
  const state = ownerState(vm)
  const scope = state.scope
  state.mountedHooks = hooks
  const proxy = scopedInstance(vm, scope, state.status.refreshing)
  try {
    await Promise.all(hooks.map((hook) => withPageRequestContext(scope, () => hook.call(proxy))))
    if (scope.isActive()) state.initialized = true
  } catch (error) {
    if (!isPageReadCanceled(error) && scope.isActive()) throw error
  }
}

/** 在动态路由加载边界包装挂载钩子，保留中断初始化后重新进入的恢复能力。 */
export function preparePageNavigationComponent(component) {
  if (!component || typeof component !== 'object') return component
  if (preparedComponents.has(component)) return preparedComponents.get(component)
  const policy = getComponentPageCachePolicy(component.name || component.__name)
  if (!policy || policy.shared) return component
  const hooks = component.mounted ? (Array.isArray(component.mounted) ? component.mounted : [component.mounted]) : []
  const prepared = { ...component }
  if (hooks.length) prepared.mounted = function () { return runMounted(this, hooks) }
  // 已有激活钩子只维护观察器；数据刷新由统一运行时负责。
  preparedComponents.set(component, prepared)
  return prepared
}

export function installPageNavigationRuntime(app) {
  app.mixin({
    computed: {
      $pageReadScope() { return resolveInstanceStatus(this).scope || null },
      $pageCacheRestoring() { return Boolean(resolveInstanceStatus(this).refreshing) },
    },
    methods: {
      $pageIsActive() {
        const state = ownerState(this)
        return state ? state.scope?.isActive() === true : true
      },
      $pageRequest(starter, options = {}) {
        const state = ownerState(this)
        return state ? state.scope.request(starter, { background: state.status.refreshing, ...options }) : starter()
      },
    },
    created() {
      const policy = getComponentPageCachePolicy(this.$options.name || this.$options.__name)
      if (!policy) return
      const ancestor = ownerState(this.$parent)
      const state = {
        vm: this, policy, owner: policy.shared || ancestor?.policy.child === policy.componentName ? ancestor : null,
        path: currentRoute(this)?.path?.toLowerCase(), cachedMode: isPageCacheable(currentRoute(this)),
        active: true, scope: null, viewIdentity: null, activationCount: 0, initialized: false, mountedHooks: [],
        refreshTicket: 0, rollbackRefresh: null, stopRouteWatch: null, cleanup: false, scroll: [], originalMethods: new Map(),
        dataCachesByScope: new WeakMap(),
        status: ensureInstanceStatus(this),
      }
      runtimes.set(this, state)
      if (!state.owner) {
        renewScope(state)
        state.stopRouteWatch = this.$watch('$route.fullPath', () => {
          if (!matchesRoute(state)) return
          const identity = resolvePageViewIdentity(currentRoute(state.vm))
          if (state.scope?.isActive() && isAnimalProfileTabOnlyChange(state.viewIdentity, identity)) {
            state.viewIdentity = identity
            return
          }
          renewScope(state)
        }, { flush: 'sync' })
      }
      for (const name of policy.wrapMethods || []) {
        const method = this.$options.methods?.[name]
        if (typeof method !== 'function') continue
        state.originalMethods.set(name, method)
        this[name] = (...args) => invokeMethod(
          this, name, args, undefined, policy.foregroundActions ? false : undefined
        )
      }
      if (state.owner) return
      // 只登记真正拥有读取作用域的页面，避免普通子组件向 KeepAlive 根传播空钩子。
      const target = this.$
      onActivated(() => {
        const firstActivation = state.activationCount++ === 0
        state.active = true
        if (!firstActivation) {
          renewScope(state)
          restoreScroll(state.scroll)
          void refreshPageForNavigation(this)
        }
      }, target)
      onDeactivated(() => {
        state.scroll = captureScroll(this)
        state.active = false
        state.scope?.close()
        state.refreshTicket += 1
        state.status.refreshing = false
        state.rollbackRefresh = null
        state.cleanup = true
        try { state.policy.deactivate?.(this) } finally { state.cleanup = false }
        invalidateSequences(this)
      }, target)
      onBeforeUnmount(() => {
        state.stopRouteWatch?.()
        state.active = false
        state.scope?.close()
      }, target)
    },
  })
}
